import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Mail, Loader2, CheckCircle2, ChevronRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/bits";
import FAQ from "@/components/FAQ";
import { api, formatApiError, WHATSAPP_LINK, CONTACT_EMAIL } from "@/lib/api";
import { SUPPORT_CATEGORIES, FAQ_PASSAGEIROS, FAQ_MOTORISTAS } from "@/data/site";

const FAQ_GERAL = [
  {
    q: "Quando o aplicativo 360Taxi estará disponível?",
    a: "O lançamento ocorrerá em etapas. Registre-se no pré-cadastro para receber um aviso quando o aplicativo estiver disponível para download.",
  },
  {
    q: "O que já está funcionando hoje?",
    a: "Na primeira fase, o único serviço operacional será o 360Taxi para automóveis, conforme a liberação do lançamento. Os demais serviços do OFF360 estão em desenvolvimento e são apresentados apenas institucionalmente.",
  },
  {
    q: "Como solicito uma corrida?",
    a: `Pelo aplicativo 360Taxi: informe partida e destino, escolha a categoria disponível na sua região e confirme. ${FAQ_PASSAGEIROS[0].a}`,
  },
  {
    q: "Como me cadastro como motorista?",
    a: "O cadastro é feito pelo aplicativo em cinco etapas: informações pessoais, documentação, dados do veículo, cadastro financeiro e análise e aprovação pela plataforma.",
  },
  {
    q: "Como excluir minha conta e meus dados?",
    a: "Acesse a página Exclusão de Conta e Dados, envie o formulário e guarde o protocolo gerado. A titularidade será verificada antes do tratamento.",
  },
];

export default function Suporte() {
  const [form, setForm] = useState({ nome: "", email: "", categoria: "", assunto: "", mensagem: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [protocol, setProtocol] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const e = {};
    if (form.nome.trim().length < 3) e.nome = "Informe seu nome completo.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Informe um e-mail válido.";
    if (!form.categoria) e.categoria = "Selecione a categoria do atendimento.";
    if (form.assunto.trim().length < 3) e.assunto = "Informe o assunto.";
    if (form.mensagem.trim().length < 10) e.mensagem = "Descreva sua solicitação (mínimo 10 caracteres).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/suporte", form);
      setProtocol(data.protocolo);
    } catch (err) {
      setServerError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      <PageHero
        eyebrow="Central de suporte"
        title="Como podemos ajudar?"
        lead="Atendimento para passageiros, motoristas e futuros usuários dos serviços OFF360."
      />

      <section className="mx-auto max-w-8xl px-4 py-16 sm:px-6 lg:px-8" data-testid="suporte-canais">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Reveal className="h-full">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="suporte-whatsapp-button"
              className="flex h-full items-center justify-between gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
            >
              <div className="flex items-center gap-4">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500 text-white">
                  <MessageCircle className="h-7 w-7" />
                </span>
                <div>
                  <p className="font-display text-lg font-bold text-slate-900">Falar com o suporte pelo WhatsApp</p>
                  <p className="text-sm text-slate-600">(11) 91749-3917 · mensagem pré-preenchida</p>
                </div>
              </div>
              <ChevronRight className="h-6 w-6 text-emerald-600" />
            </a>
          </Reveal>
          <Reveal delay={0.08} className="h-full">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="suporte-email-button"
              className="flex h-full items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
            >
              <div className="flex items-center gap-4">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-orange text-white">
                  <Mail className="h-7 w-7" />
                </span>
                <div>
                  <p className="font-display text-lg font-bold text-slate-900">E-mail oficial</p>
                  <p className="text-sm text-slate-600">{CONTACT_EMAIL}</p>
                </div>
              </div>
              <ChevronRight className="h-6 w-6 text-brand-orange" />
            </a>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-12 lg:grid-cols-[1.2fr_0.8fr]">
          <Reveal>
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm sm:p-10" data-testid="suporte-form-card">
              {protocol ? (
                <div className="py-8 text-center" data-testid="support-protocol-result">
                  <CheckCircle2 className="mx-auto h-14 w-14 text-brand-green" />
                  <h3 className="mt-4 font-display text-2xl font-bold text-slate-900">Solicitação registrada!</h3>
                  <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
                    Guarde o número de protocolo abaixo para acompanhar o atendimento.
                  </p>
                  <p className="mx-auto mt-6 w-fit rounded-full bg-brand-obsidian px-8 py-3 font-mono text-lg font-bold text-brand-amber">
                    {protocol}
                  </p>
                  <button
                    type="button"
                    data-testid="suporte-nova-solicitacao"
                    onClick={() => {
                      setProtocol("");
                      setForm({ nome: "", email: "", categoria: "", assunto: "", mensagem: "" });
                    }}
                    className="mt-8 rounded-full border border-slate-300 px-6 py-3 text-sm font-bold text-slate-700 transition-colors hover:border-brand-orange hover:text-brand-orange"
                  >
                    Enviar outra solicitação
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} noValidate data-testid="suporte-form">
                  <h2 className="font-display text-2xl font-bold text-slate-900">Abra uma solicitação</h2>
                  <p className="mt-2 text-sm text-slate-500">
                    Preencha os campos abaixo. Ao final, você receberá um número de protocolo na tela.
                  </p>
                  <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <Field label="Nome completo *" error={errors.nome}>
                      <input
                        data-testid="suporte-nome-input"
                        type="text"
                        value={form.nome}
                        onChange={set("nome")}
                        className="input-off"
                        placeholder="Seu nome completo"
                      />
                    </Field>
                    <Field label="E-mail *" error={errors.email}>
                      <input
                        data-testid="suporte-email-input"
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        className="input-off"
                        placeholder="voce@email.com"
                      />
                    </Field>
                    <Field label="Categoria do atendimento *" error={errors.categoria}>
                      <select data-testid="suporte-categoria-select" value={form.categoria} onChange={set("categoria")} className="input-off">
                        <option value="">Selecione</option>
                        {SUPPORT_CATEGORIES.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Assunto *" error={errors.assunto}>
                      <input
                        data-testid="suporte-assunto-input"
                        type="text"
                        value={form.assunto}
                        onChange={set("assunto")}
                        className="input-off"
                        placeholder="Resumo do assunto"
                      />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Mensagem *" error={errors.mensagem}>
                        <textarea
                          data-testid="suporte-mensagem-input"
                          rows={5}
                          value={form.mensagem}
                          onChange={set("mensagem")}
                          className="input-off resize-y"
                          placeholder="Descreva sua solicitação com o máximo de detalhes."
                        />
                      </Field>
                    </div>
                  </div>
                  {serverError && (
                    <p className="mt-5 rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" data-testid="suporte-server-error">
                      {serverError}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="support-form-submit"
                    className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-4 text-base font-bold text-white shadow-lg shadow-brand-orange/25 transition-all hover:bg-brand-orangedark disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting && <Loader2 className="h-5 w-5 animate-spin" />}
                    Enviar solicitação
                  </button>
                </form>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="space-y-5">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-7">
                <h3 className="font-display text-lg font-bold text-slate-900">Categorias de atendimento</h3>
                <ul className="mt-4 grid grid-cols-1 gap-2 text-sm text-slate-600">
                  {SUPPORT_CATEGORIES.map((c) => (
                    <li key={c} className="flex items-center gap-2.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-orange" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-7">
                <h3 className="font-display text-lg font-bold text-slate-900">Exclusão de conta</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Para solicitar exclusão de conta e dados, utilize o formulário próprio, com geração de protocolo.
                </p>
                <Link
                  to="/excluir-conta"
                  data-testid="suporte-link-excluir-conta"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-brand-orange hover:underline"
                >
                  Ir para Exclusão de Conta <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-slate-50 py-20" id="perguntas-frequentes" data-testid="faq-suporte">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionHead eyebrow="Perguntas frequentes" title="As dúvidas mais comuns." />
          </Reveal>
          <Reveal delay={0.1}>
            <FAQ items={[...FAQ_GERAL, ...FAQ_MOTORISTAS.slice(0, 2)]} idPrefix="faq-suporte" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <div className="mt-1.5">{children}</div>
      {error && <span className="mt-1 block text-xs font-semibold text-red-600">{error}</span>}
    </label>
  );
}
